import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Check, X, Building, RefreshCw, Shield } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { id: 'ce', label: 'Comité d\'entreprise (CE)', color: 'var(--admin-gold)' },
  { id: 'entreprise', label: 'Entreprise', color: '#C5D8F9' },
  { id: 'association', label: 'Association', color: '#F9C5D1' },
  { id: 'groupe', label: 'Groupe Market', color: '#3DDC97' },
];

export default function AdminCE() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    nom_entreprise: '', code: '', type: 'ce',
    remise_pct: 15, beneficiaires_max: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState('');
  const [validateResult, setValidateResult] = useState(null);

  useEffect(() => { fetchCodes(); }, []);

  async function fetchCodes() {
    setLoading(true);
    const res = await fetch(`${BACKEND}/api/ce/codes`);
    const data = await res.json();
    setCodes(data.codes || []);
    setLoading(false);
  }

  async function createCode() {
    if (!form.nom_entreprise || !form.code) { toast.error('Nom et code requis'); return; }
    setSaving(true);
    const res = await fetch(`${BACKEND}/api/ce/codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        remise_pct: parseInt(form.remise_pct) || 15,
        beneficiaires_max: form.beneficiaires_max ? parseInt(form.beneficiaires_max) : null
      })
    });
    const data = await res.json();
    if (data.success) {
      toast.success('Code créé !');
      setShowForm(false);
      setForm({ nom_entreprise: '', code: '', type: 'ce', remise_pct: 15, beneficiaires_max: '', notes: '' });
      fetchCodes();
    } else {
      toast.error(data.detail || 'Erreur');
    }
    setSaving(false);
  }

  async function deleteCode(id) {
    if (!window.confirm('Supprimer ce code ?')) return;
    await fetch(`${BACKEND}/api/ce/codes/${id}`, { method: 'DELETE' });
    toast.success('Code supprimé');
    fetchCodes();
  }

  async function toggleStatus(code) {
    const newStatus = code.statut === 'actif' ? 'inactif' : 'actif';
    await fetch(`${BACKEND}/api/ce/codes/${code.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut: newStatus })
    });
    fetchCodes();
  }

  async function testValidate() {
    if (!validating) return;
    const res = await fetch(`${BACKEND}/api/ce/validate-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: validating })
    });
    const data = await res.json();
    setValidateResult(data);
  }

  async function seedDemo() {
    await fetch(`${BACKEND}/api/ce/seed-codes`);
    toast.success('Codes démo créés');
    fetchCodes();
  }

  const inputStyle = { background: '#1C1C1C', border: '1px solid #2A2A2A', color: 'var(--admin-text)' };

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Codes CE & Remises</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Codes entreprises et remises dédiées</p>
      </div>
>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-xl font-bold" style={{ color: 'var(--admin-text)' }}>
            Gestion CE / Entreprise / Association
          </h2>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
            Codes d'accès aux tarifs préférentiels — invisible côté client public
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={seedDemo} className="px-3 py-2 rounded-xl text-xs"
            style={{ background: 'var(--admin-surface)', color: 'var(--admin-muted)', border: '1px solid #2A2A2A' }}>
            Codes démo
          </button>
          <button onClick={() => setShowForm(true)}
            className="btn-gold px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
            <Plus size={15} /> Nouveau code
          </button>
        </div>
      </div>

      {/* Règle affichée */}
      <div className="p-3 rounded-xl mb-5"
        style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid var(--admin-teal-line)' }}>
        <div className="flex items-start gap-2">
          <Shield size={14} style={{ color: 'var(--admin-teal)', flexShrink: 0, marginTop: '1px' }} />
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>
            <strong style={{ color: 'var(--admin-teal)' }}>Règle tarifaire :</strong> Le client voit uniquement le tarif public (particulier) sur les fiches produit. Le tarif préférentiel s'applique automatiquement quand il est connecté avec le bon profil OU après saisie d'un code valide à l'inscription.
          </p>
        </div>
      </div>

      {/* Formulaire création */}
      {showForm && (
        <div className="p-5 rounded-xl mb-5" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(212,175,55,0.3)' }}>
          <h3 className="font-semibold text-sm mb-4" style={{ color: 'var(--admin-gold)' }}>Nouveau code</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Nom entreprise *</label>
              <input value={form.nom_entreprise} onChange={e => setForm(f => ({ ...f, nom_entreprise: e.target.value }))}
                placeholder="Ex: Mairie de Figeac" className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Code d'accès *</label>
              <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="Ex: CE-MAIRIE-2026" className="w-full px-3 py-2.5 rounded-xl text-sm font-mono-ose" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle}>
                {TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Remise (%)</label>
              <input type="number" min="0" max="50" value={form.remise_pct}
                onChange={e => setForm(f => ({ ...f, remise_pct: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Bénéficiaires max (vide = illimité)</label>
              <input type="number" value={form.beneficiaires_max} onChange={e => setForm(f => ({ ...f, beneficiaires_max: e.target.value }))}
                placeholder="50" className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>Notes internes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Contrat, contact..." className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl text-sm"
              style={{ background: '#2A2A2A', color: 'rgba(255,255,255,0.6)' }}>Annuler</button>
            <button onClick={createCode} disabled={saving}
              className="flex-1 btn-gold py-2 rounded-xl text-sm font-semibold disabled:opacity-40">
              {saving ? 'Création...' : 'Créer le code'}
            </button>
          </div>
        </div>
      )}

      {/* Test validation code */}
      <div className="p-4 rounded-xl mb-5" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--admin-muted)' }}>Tester un code</p>
        <div className="flex gap-2">
          <input value={validating} onChange={e => setValidating(e.target.value.toUpperCase())}
            placeholder="Saisir un code..." className="flex-1 px-3 py-2 rounded-xl text-sm font-mono-ose" style={inputStyle} />
          <button onClick={testValidate} className="px-4 py-2 rounded-xl text-xs font-semibold"
            style={{ background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' }}>Tester</button>
        </div>
        {validateResult && (
          <div className="mt-2 p-2 rounded-lg"
            style={{ background: validateResult.valid ? 'rgba(61,220,151,0.08)' : 'rgba(255,92,108,0.08)', border: `1px solid ${validateResult.valid ? 'rgba(61,220,151,0.2)' : 'rgba(255,92,108,0.2)'}` }}>
            <p className="text-xs" style={{ color: validateResult.valid ? '#3DDC97' : '#FF5C6C' }}>
              {validateResult.valid ? '✓' : '✗'} {validateResult.message}
            </p>
          </div>
        )}
      </div>

      {/* Liste codes */}
      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin w-8 h-8 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} /></div>
      ) : codes.length === 0 ? (
        <div className="p-8 rounded-xl text-center" style={{ background: 'var(--admin-surface)', border: '1px solid #2A2A2A' }}>
          <Building size={32} className="mx-auto mb-2" style={{ color: 'rgba(255,255,255,0.15)' }} />
          <p className="text-sm" style={{ color: 'var(--admin-faint)' }}>Aucun code CE configuré</p>
          <button onClick={seedDemo} className="text-xs mt-2" style={{ color: 'var(--admin-gold)' }}>Créer des codes démo</button>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #2A2A2A' }}>
          {codes.map((c, i) => {
            const typeInfo = TYPES.find(t => t.id === c.type) || TYPES[0];
            return (
              <div key={c.id} className="flex items-center gap-3 px-4 py-3"
                style={{ background: i % 2 === 0 ? '#161616' : '#121212', borderBottom: '1px solid rgba(42,42,42,0.5)' }}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>{c.nom_entreprise}</p>
                    <span className="px-2 py-0.5 rounded-full text-xs"
                      style={{ background: `${typeInfo.color}15`, color: typeInfo.color }}>
                      {typeInfo.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-ose text-xs" style={{ color: 'var(--admin-gold)' }}>{c.code}</span>
                    <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>-{c.remise_pct}%</span>
                    <span className="text-xs" style={{ color: 'var(--admin-faint)' }}>
                      {c.nb_utilisations || 0}{c.beneficiaires_max ? `/${c.beneficiaires_max}` : ''} utilisation(s)
                    </span>
                  </div>
                </div>
                <button onClick={() => toggleStatus(c)}
                  className="px-2 py-1 rounded-lg text-xs"
                  style={{ background: c.statut === 'actif' ? 'rgba(61,220,151,0.12)' : 'rgba(255,92,108,0.12)', color: c.statut === 'actif' ? '#3DDC97' : '#FF5C6C' }}>
                  {c.statut === 'actif' ? 'Actif' : 'Inactif'}
                </button>
                <button onClick={() => deleteCode(c.id)} className="p-1.5 rounded-lg"
                  style={{ background: 'rgba(255,92,108,0.1)', color: '#FF5C6C' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
